/**
 * EPIC-DM01-C: Demo Capture Page (Redesigned)
 * Single swing upload with inline pose extraction + analysis trigger
 *
 * Design Reference: Figma Capture (iPad + phone)
 * Step 3 of 4 — camera viewfinder, setup tips, demo UI
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDemoMode, type DemoSession } from '@/hooks/useDemoMode';
import { useDemoCapture, type DemoAttempt, type UploadProgress } from '@/hooks/useDemoCapture';
import { useClientPoseExtraction } from '@/features/upload/useClientPoseExtraction';
import { usePipelineDiagnostics } from '@/hooks/usePipelineDiagnostics';
import { callEdgeFunction } from '@/lib/edgeFunctionClient';
import {
  AlertCircle,
  ArrowLeft,
  Camera,
  Check,
  Loader2,
  RefreshCw,
  Upload,
  Video,
  X,
  Lightbulb,
  Eye,
  Sun,
  Smartphone,
} from 'lucide-react';
import { DemoButton, DemoCard, DemoProgress } from '@/components/demo/ui';

const MAX_ATTEMPTS = 1;

const SETUP_TIPS = [
  { icon: Eye, text: 'Face-on or down-the-line camera angle' },
  { icon: Smartphone, text: 'Full body in frame — head to feet' },
  { icon: Sun, text: 'Good lighting, stable device' },
];

interface AttemptSlotProps {
  index: number;
  attempt: DemoAttempt | undefined;
  isCurrentUpload: boolean;
  isDisabled: boolean;
  onUpload: (index: number, file: File) => void;
  onRetry: (index: number) => void;
}

function AttemptSlot({
  index,
  attempt,
  isCurrentUpload,
  isDisabled,
  onUpload,
  onRetry,
}: AttemptSlotProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClick = () => {
    if (!isDisabled && !attempt) {
      inputRef.current?.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onUpload(index, file);
      e.target.value = '';
    }
  };

  const handleRetryClick = () => {
    onRetry(index);
    // Synchronous click() keeps the user-gesture chain intact on iOS Safari,
    // which blocks programmatic file-picker opens outside gesture handlers.
    inputRef.current?.click();
  };

  // Uploaded success
  if (attempt?.status === 'uploaded') {
    return (
      <div className="flex items-center gap-3 bg-demo-green-light rounded-2xl p-4 border border-demo-green/20">
        <div className="h-12 w-12 rounded-xl bg-demo-green flex items-center justify-center flex-shrink-0">
          <Check className="h-6 w-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-demo-green-darkest">Swing {index + 1} Uploaded</p>
          <p className="text-xs text-demo-green-dark">Ready for selection</p>
        </div>
      </div>
    );
  }

  // Uploading
  if (attempt?.status === 'uploading') {
    const progress = attempt.progress ?? 0;
    return (
      <div className="rounded-2xl border-2 border-demo-green/30 p-4 bg-white">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-12 w-12 rounded-xl bg-demo-green-light flex items-center justify-center flex-shrink-0">
            <Loader2 className="h-6 w-6 text-demo-green animate-spin" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-demo-text">Uploading Swing {index + 1}...</p>
            <p className="text-xs text-demo-text-muted">{progress}% complete</p>
          </div>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-demo-green h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    );
  }

  // Failed — input as sibling for mobile compatibility
  if (attempt?.status === 'failed') {
    return (
      <>
        <input
          ref={inputRef}
          type="file"
          accept="video/*"
          className="hidden"
          onChange={handleFileChange}
          disabled={isDisabled}
        />
        <div className="flex items-center gap-3 bg-demo-error-light rounded-2xl p-4 border border-demo-error/20">
          <div className="h-12 w-12 rounded-xl bg-demo-error flex items-center justify-center flex-shrink-0">
            <X className="h-6 w-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-demo-error">Upload Failed</p>
            <p className="text-xs text-demo-text-muted">{attempt.error || 'Please try again'}</p>
          </div>
          <DemoButton variant="outline" size="sm" onClick={handleRetryClick} disabled={isDisabled}>
            <RefreshCw className="h-4 w-4 mr-1" />
            Retry
          </DemoButton>
        </div>
      </>
    );
  }

  // Empty slot — input MUST be a sibling of the button (not nested) for mobile compatibility
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="video/*"
        className="hidden"
        onChange={handleFileChange}
        disabled={isDisabled}
      />
      <button
        type="button"
        onClick={handleClick}
        disabled={isDisabled}
        className={`
          w-full flex items-center gap-3 rounded-2xl border-2 border-dashed p-4 transition-all text-left
          ${isDisabled
            ? 'opacity-50 cursor-not-allowed border-gray-200'
            : 'border-gray-300 hover:border-demo-green/50 hover:bg-demo-green-light/30 cursor-pointer active:scale-[0.98]'
          }
        `}
      >
        <div className="h-12 w-12 rounded-xl bg-demo-surface-alt flex items-center justify-center flex-shrink-0">
          <Video className="h-6 w-6 text-demo-text-muted" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-demo-text">Swing {index + 1}</p>
          <p className="text-xs text-demo-text-muted">Tap to record or upload</p>
        </div>
        <Upload className="h-5 w-5 text-demo-text-muted flex-shrink-0" />
      </button>
    </>
  );
}

export default function DemoCapture() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const viewfinderInputRef = useRef<HTMLInputElement>(null);

  const {
    canAccessDemoMode,
    isDemoModeEnabled,
    isLoading: isLoadingConfig,
    getSession,
  } = useDemoMode();

  const {
    attempts,
    isUploading,
    currentAttempt,
    uploadAttempt,
    retryAttempt,
    initializeAttempts,
    hasUploadedAttempts,
  } = useDemoCapture(sessionId || '');

  const { extractPose, progress: poseProgress } = useClientPoseExtraction();
  const { recordStage } = usePipelineDiagnostics();

  const [session, setSession] = useState<DemoSession | null>(null);
  const [isLoadingSession, setIsLoadingSession] = useState(true);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [isPoseExtracting, setIsPoseExtracting] = useState(false);
  const [pipelineError, setPipelineError] = useState<string | null>(null);

  const PIPELINE_TYPE_DEMO = 'demo' as const;

  useEffect(() => {
    async function loadSession() {
      if (!sessionId) {
        setSessionError('No session ID provided');
        setIsLoadingSession(false);
        return;
      }

      try {
        const loadedSession = await getSession(sessionId);
        if (!loadedSession) {
          setSessionError('Session not found');
        } else if (!loadedSession.prospect_user_id) {
          setSessionError('Intake must be completed before capture');
        } else {
          setSession(loadedSession);
          const meta = loadedSession.meta as { attempts?: DemoAttempt[] };
          if (meta?.attempts) {
            initializeAttempts(meta.attempts);
          }
        }
      } catch (err) {
        console.error('Failed to load session:', err);
        setSessionError('Failed to load session');
      } finally {
        setIsLoadingSession(false);
      }
    }

    loadSession();
  }, [sessionId, getSession, initializeAttempts]);

  /**
   * After upload completes, auto-select the swing, run pose extraction,
   * and trigger analysis via RPC — then navigate to processing.
   *
   * Falls back to server-side pipeline (demo-run-trigger) when client-side
   * MediaPipe extraction fails (e.g. iOS Safari lacks WebGL support).
   */
  const startPipeline = useCallback(async (attempt: DemoAttempt) => {
    if (!sessionId) return;
    setIsPoseExtracting(true);
    setPipelineError(null);

    try {
      // Mark swing as selected in session meta
      await callEdgeFunction('demo-run-select', 'POST', {
        demo_session_id: sessionId,
        run_id: attempt.run_id,
        attempt: attempt.attempt,
      });

      try {
        recordStage({ runId: attempt.run_id, pipelineType: PIPELINE_TYPE_DEMO, stage: 'upload_initiated', clientRecordedAt: new Date().toISOString() });
        recordStage({ runId: attempt.run_id, pipelineType: PIPELINE_TYPE_DEMO, stage: 'cf_upload_complete', clientRecordedAt: new Date().toISOString() });
        recordStage({ runId: attempt.run_id, pipelineType: PIPELINE_TYPE_DEMO, stage: 'pose_extraction_start', clientRecordedAt: new Date().toISOString() });
      } catch (_) { /* non-blocking */ }

      // Wait for Cloudflare to finish transcoding (match UploadScreen's 5s delay)
      await new Promise(resolve => setTimeout(resolve, 5000));

      const poseResult = await extractPose(attempt.cf_stream_uid, attempt.run_id, {
        demoSessionId: sessionId,
      });

      if (poseResult.success) {
        try {
          recordStage({ runId: attempt.run_id, pipelineType: PIPELINE_TYPE_DEMO, stage: 'pose_extraction_complete', clientRecordedAt: new Date().toISOString(), meta: { framesProcessed: poseResult.framesProcessed, durationMs: poseResult.durationMs } });
          recordStage({ runId: attempt.run_id, pipelineType: PIPELINE_TYPE_DEMO, stage: 'pose_saved', clientRecordedAt: new Date().toISOString() });
        } catch (_) { /* non-blocking */ }
        console.log('[DemoCapture] Pose extraction + analysis trigger complete:', {
          frames: poseResult.framesProcessed,
          durationMs: poseResult.durationMs,
        });
        navigate(`/demo/processing/${sessionId}`);
      } else {
        throw new Error(poseResult.error || 'Pose extraction failed');
      }
    } catch (error) {
      const errMsg = error instanceof Error ? error.message : String(error);
      console.warn('[DemoCapture] Client-side pose failed, falling back to server pipeline:', errMsg);

      try {
        recordStage({ runId: attempt.run_id, pipelineType: PIPELINE_TYPE_DEMO, stage: 'client_pose_fallback', clientRecordedAt: new Date().toISOString(), meta: { reason: errMsg } });
      } catch (_) { /* non-blocking */ }

      try {
        await callEdgeFunction('demo-run-trigger', 'POST', {
          demo_session_id: sessionId,
        });
        console.log('[DemoCapture] Server-side pipeline triggered (fallback)');
        navigate(`/demo/processing/${sessionId}`);
      } catch (fallbackErr) {
        console.error('[DemoCapture] Server-side fallback also failed:', fallbackErr);
        setPipelineError(fallbackErr instanceof Error ? fallbackErr.message : 'Analysis failed');
        setIsPoseExtracting(false);
      }
    }
  }, [sessionId, extractPose, navigate, recordStage]);

  const handleUpload = useCallback(async (index: number, file: File) => {
    try {
      const runId = await uploadAttempt(index, file, (progress: UploadProgress) => {
        console.log(`[DemoCapture] Upload progress: ${progress.pct}%`);
      });

      // After upload, auto-start the pipeline
      const uploadedAttempt = {
        attempt: index,
        run_id: runId,
        cf_stream_uid: '', // Will be populated from attempts state
        status: 'uploaded' as const,
        created_at: new Date().toISOString(),
      };

      // Get the cf_stream_uid from the attempts state (set by useDemoCapture)
      // We need a small delay for state to settle
      setTimeout(() => {
        // Read from DOM-updated state won't work in this closure;
        // we stored cf_stream_uid in the upload flow already
      }, 0);
    } catch (error) {
      console.error('[DemoCapture] Upload failed:', error);
      setPipelineError(error instanceof Error ? error.message : 'Upload failed');
    }
  }, [uploadAttempt]);

  // After an upload succeeds, auto-start the pipeline
  const pipelineTriggeredRef = useRef(false);
  useEffect(() => {
    if (pipelineTriggeredRef.current || isPoseExtracting) return;
    const uploaded = attempts.find(a => a.status === 'uploaded' && a.run_id && a.cf_stream_uid);
    if (uploaded) {
      pipelineTriggeredRef.current = true;
      startPipeline(uploaded);
    }
  }, [attempts, isPoseExtracting, startPipeline]);

  const handleRetry = useCallback((index: number) => {
    pipelineTriggeredRef.current = false;
    setPipelineError(null);
  }, []);

  // The viewfinder always targets slot 0
  const hasAttempt = attempts.length > 0;

  const handleViewfinderTap = useCallback(() => {
    if (isUploading || hasAttempt || isPoseExtracting) return;
    viewfinderInputRef.current?.click();
  }, [isUploading, hasAttempt, isPoseExtracting]);

  const handleViewfinderFile = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleUpload(0, file);
      }
      e.target.value = '';
    },
    [handleUpload],
  );

  // ── Pose extraction in progress ───────────────────────────────
  if (isPoseExtracting) {
    return (
      <div className="demo-mode min-h-screen bg-demo-surface flex items-center justify-center p-4">
        <DemoCard className="max-w-md w-full">
          <div className="p-6 text-center">
            <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-demo-green" />
            <h2 className="text-xl font-bold text-demo-text mb-2">Analyzing Your Swing</h2>
            <p className="text-demo-text-muted mb-4">
              {poseProgress.status === 'loading_video' && 'Loading video...'}
              {poseProgress.status === 'initializing_mediapipe' && 'Initializing AI models...'}
              {poseProgress.status === 'extracting' && `Extracting pose data... (${poseProgress.framesProcessed} frames)`}
              {poseProgress.status === 'saving' && 'Saving analysis...'}
              {poseProgress.status === 'triggering_analysis' && 'Starting AI coaching analysis...'}
              {!['loading_video', 'initializing_mediapipe', 'extracting', 'saving', 'triggering_analysis'].includes(poseProgress.status) && 'Processing...'}
            </p>
            <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
              <div 
                className="bg-demo-green h-2 rounded-full transition-all duration-300"
                style={{ width: `${poseProgress.progress}%` }}
              />
            </div>
            <p className="text-sm text-demo-text-muted">{poseProgress.progress}% complete</p>
          </div>
        </DemoCard>
      </div>
    );
  }

  // ── Loading ────────────────────────────────────────────────────
  if (isLoadingConfig || isLoadingSession) {
    return (
      <div className="demo-mode min-h-screen bg-demo-surface flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-demo-green" />
          <p className="text-demo-text-muted">Loading...</p>
        </div>
      </div>
    );
  }

  // ── Not authorized ─────────────────────────────────────────────
  if (!canAccessDemoMode || !isDemoModeEnabled) {
    return (
      <div className="demo-mode min-h-screen bg-demo-surface flex items-center justify-center p-4">
        <DemoCard className="max-w-md w-full">
          <div className="p-6 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-demo-error" />
            <h2 className="text-xl font-bold text-demo-text mb-2">Access Denied</h2>
            <p className="text-demo-text-muted mb-4">
              {!canAccessDemoMode
                ? 'You do not have permission to access Demo Mode.'
                : 'Demo Mode is currently disabled.'}
            </p>
            <DemoButton variant="outline" onClick={() => navigate('/home')}>
              Return to Home
            </DemoButton>
          </div>
        </DemoCard>
      </div>
    );
  }

  // ── Session error ──────────────────────────────────────────────
  if (sessionError || !session) {
    return (
      <div className="demo-mode min-h-screen bg-demo-surface flex items-center justify-center p-4">
        <DemoCard className="max-w-md w-full">
          <div className="p-6 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-demo-error" />
            <h2 className="text-xl font-bold text-demo-text mb-2">Session Error</h2>
            <p className="text-demo-text-muted mb-4">{sessionError || 'Unable to load session'}</p>
            <DemoButton variant="outline" onClick={() => navigate('/demo')}>
              Return to Demo Landing
            </DemoButton>
          </div>
        </DemoCard>
      </div>
    );
  }

  // ── Main capture UI ────────────────────────────────────────────
  return (
    <div className="demo-mode min-h-screen bg-demo-surface">
      {/* Sticky header */}
      <header className="sticky top-0 z-10 bg-white border-b border-gray-100 shadow-demo-sm">
        <div className="max-w-xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(`/demo/intake/${sessionId}`)}
            disabled={isUploading}
            className="h-10 w-10 flex items-center justify-center rounded-xl text-demo-text-muted hover:bg-demo-surface-alt"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-demo-text truncate">Record Your Swing</h1>
            <p className="text-xs text-demo-text-muted">Step 3 of 4</p>
          </div>
        </div>
      </header>

      {/* Progress bar */}
      <div className="max-w-xl mx-auto px-4 pt-4">
        <DemoProgress mode="bar" current={3} total={4} />
      </div>

      {/* Content */}
      <main className="max-w-xl mx-auto px-4 py-6 pb-32">
        {/* Camera viewfinder area — tapping opens camera for next empty slot */}
        <input
          ref={viewfinderInputRef}
          type="file"
          accept="video/*"
          className="hidden"
          onChange={handleViewfinderFile}
          disabled={isUploading || hasAttempt || isPoseExtracting}
        />
        <button
          type="button"
          onClick={handleViewfinderTap}
          disabled={isUploading || hasAttempt || isPoseExtracting}
          className={`
            relative w-full bg-gray-900 rounded-2xl overflow-hidden aspect-[4/3] mb-6 flex items-center justify-center
            ${isUploading || hasAttempt || isPoseExtracting
              ? 'opacity-60 cursor-not-allowed'
              : 'cursor-pointer active:scale-[0.99] transition-transform'}
          `}
        >
          <div className="absolute inset-4 border-2 border-dashed border-white/30 rounded-xl" />
          <div className="text-center z-10">
            <Camera className="h-12 w-12 text-white/60 mx-auto mb-3" />
            <p className="text-white/80 font-medium text-sm">
              {hasAttempt ? 'Swing uploaded' : 'Tap to record your swing'}
            </p>
            <p className="text-white/50 text-xs mt-1">Full body visible, head to feet</p>
          </div>
          {/* Corner guides */}
          <div className="absolute top-4 left-4 w-8 h-8 border-t-2 border-l-2 border-white/50 rounded-tl-lg" />
          <div className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-white/50 rounded-tr-lg" />
          <div className="absolute bottom-4 left-4 w-8 h-8 border-b-2 border-l-2 border-white/50 rounded-bl-lg" />
          <div className="absolute bottom-4 right-4 w-8 h-8 border-b-2 border-r-2 border-white/50 rounded-br-lg" />
        </button>

        {/* Quick Setup Tips */}
        <DemoCard variant="highlight" className="mb-6">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-9 h-9 bg-demo-gold-light rounded-lg flex items-center justify-center mt-0.5">
              <Lightbulb className="h-5 w-5 text-demo-gold-dark" />
            </div>
            <div>
              <h3 className="font-semibold text-demo-text text-sm mb-2">Quick Setup Tips</h3>
              <ul className="space-y-2">
                {SETUP_TIPS.map((tip) => (
                  <li key={tip.text} className="flex items-center gap-2 text-xs text-demo-text-muted">
                    <tip.icon className="h-4 w-4 text-demo-gold-dark flex-shrink-0" />
                    {tip.text}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </DemoCard>

        {/* Attempt slot (single swing) */}
        <div className="space-y-3 mb-6">
          <AttemptSlot
            index={0}
            attempt={attempts.find((a) => a.attempt === 0)}
            isCurrentUpload={currentAttempt === 0}
            isDisabled={isUploading || isPoseExtracting}
            onUpload={handleUpload}
            onRetry={handleRetry}
          />
        </div>

        {/* Pipeline error */}
        {pipelineError && (
          <DemoCard variant="warning" className="mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-demo-error mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-demo-error text-sm">Analysis Error</p>
                <p className="text-xs text-demo-error/80">{pipelineError}</p>
              </div>
            </div>
          </DemoCard>
        )}

        {/* Help text */}
        <p className="text-center text-xs text-demo-text-muted mb-6">
          Upload a swing video to start your AI analysis
        </p>
      </main>
    </div>
  );
}
